// Is OUR backend answering (wiki/feature-offline-fallback-cues.md §1)? No steady-state
// polling: a failed real request opens a suspect streak that confirm-probes, and a
// recovery probe runs only while offline.

import { writable, type Readable } from 'svelte/store';
import { API_ORIGIN, setReachabilityListener } from './client';

export interface BackendHealthState {
  online: boolean;
}

// Users reach the North-American server over high-RTT, jittery mobile links (e.g.
// from Australia): a slow round trip or a momentary drop is not an outage. Offline
// needs several failures spread over a real span, with no success in between.
export const HEALTH_RULE = {
  probeTimeoutMs: 10_000,
  offlineAfterFailures: 3,
  offlineAfterMs: 10_000,
  confirmProbeMs: 5_000,
  recoveryIntervalMs: 15_000,
} as const;

// The proxy answers these when it cannot reach the app — unreachable, not a server error.
const GATEWAY_STATUSES = new Set([502, 503, 504]);

export interface BackendHealthDeps {
  fetchImpl?: typeof fetch;
  healthUrl?: string;
}

export interface BackendHealthController {
  readonly store: Readable<BackendHealthState>;
  probe(): Promise<void>;
  report(reachable: boolean): void;
  start(): void;
  stop(): void;
}

export function createBackendHealth(deps: BackendHealthDeps = {}): BackendHealthController {
  const healthUrl = deps.healthUrl ?? `${API_ORIGIN}/health`;

  let online = true;
  let failures = 0;
  let firstFailureAt = 0;
  let probing = false;
  let started = false;
  let confirmTimer: ReturnType<typeof setTimeout> | null = null;
  let recoveryInterval: ReturnType<typeof setInterval> | null = null;
  const internal = writable<BackendHealthState>({ online });

  function clearConfirm(): void {
    if (confirmTimer !== null) clearTimeout(confirmTimer);
    confirmTimer = null;
  }

  function setOnline(next: boolean): void {
    failures = 0;
    clearConfirm();
    if (next === online) return;
    online = next;
    internal.set({ online });
    if (next) {
      if (recoveryInterval !== null) clearInterval(recoveryInterval);
      recoveryInterval = null;
    } else if (started && recoveryInterval === null) {
      recoveryInterval = setInterval(() => void probe(), HEALTH_RULE.recoveryIntervalMs);
    }
  }

  function fail(): void {
    if (!online) return;
    const now = Date.now();
    if (failures === 0) firstFailureAt = now;
    failures += 1;
    if (failures >= HEALTH_RULE.offlineAfterFailures && now - firstFailureAt >= HEALTH_RULE.offlineAfterMs) {
      setOnline(false);
    } else if (started && confirmTimer === null) {
      confirmTimer = setTimeout(() => {
        confirmTimer = null;
        void probe();
      }, HEALTH_RULE.confirmProbeMs);
    }
  }

  function report(reachable: boolean): void {
    if (reachable) setOnline(true);
    else fail();
  }

  async function probe(): Promise<void> {
    const fetchImpl = deps.fetchImpl ?? globalThis.fetch;
    if (!fetchImpl || probing) return;
    probing = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), HEALTH_RULE.probeTimeoutMs);
    try {
      const res = await fetchImpl(healthUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      report(!GATEWAY_STATUSES.has(res.status));
    } catch {
      report(false);
    } finally {
      clearTimeout(timer);
      probing = false;
    }
  }

  const onOnline = (): void => void probe();
  // The device itself has no network — nothing to wait for.
  const onOffline = (): void => setOnline(false);

  function start(): void {
    if (started) return;
    started = true;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) onOffline();
    else void probe();
  }

  function stop(): void {
    if (!started) return;
    started = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    }
    clearConfirm();
    if (recoveryInterval !== null) clearInterval(recoveryInterval);
    recoveryInterval = null;
  }

  return { store: { subscribe: internal.subscribe }, probe, report, start, stop };
}

export const backendHealth: BackendHealthController = createBackendHealth();
setReachabilityListener(backendHealth.report);

if (import.meta.hot) {
  import.meta.hot.dispose(() => backendHealth.stop());
}
